import React, { useState, MouseEvent, ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { deviceMedia, USER_NAME_LOCAL_STORAGE_KEY } from 'appConstants';
import Modal from 'shared-components/Modal';
import { googleAnalytics } from 'services/googleAnalytics';
import { useSelector } from 'react-redux';
import { getBrandingLayout } from 'store/branding';
import { getColor } from '../theme';
import { bold, resetButtonStyles, textRegular } from '../mixins';
import { ProfileIcon } from './ProfileIcon';
import { EuLanguageFlag } from './EuLanguageFlag';
import DOMPurify from 'dompurify';
import { localizationService } from 'services/localization';
import { clearEntryIntent } from 'store/comoLink/entryIntent';

const prepareName = (name: string) =>
  name
    .split(' ')
    .map((word: string) => {
      // 22 is the maximum symbols amount that can be rendered in account dropdown
      if (word.length > 22) {
        return `<div style="word-break: break-all;">${word}</div>`;
      }
      return word;
    })
    .join(' ');

interface AccountOptionsTranslations {
  welcomeLabel: string;
  logOutModalHeader: string;
  logOutModalBody: string;
  logOutModalOkButtonText: string;
  logOutModalCancelButtonText: string;
  logOutLinkText: string;
  contactSupportLabel: string;
  languageLabel: string;
}

const FullName = styled.div`
  ${bold};
  color: ${props => getColor(props, 'color1')};
  width: 100%;
  text-transform: capitalize;
`;

interface Initials {
  firstLetter: string;
  lastLetter: string;
}

interface AccountOptionsProps {
  className?: string;
  firstName: string;
  lastName: string;
  isUserNameIsNotEmpty: boolean;
  i18n: AccountOptionsTranslations;
  supportPageRoute?: string;
}

const Link = ({ to, children }: { to: string; children: ReactNode }) => (
  <RouterLink to={to} target="_blank">
    {children}
  </RouterLink>
);

export const AccountOptionsComponent = (props: AccountOptionsProps & Initials) => {
  const { className, firstName, lastName, firstLetter, lastLetter, isUserNameIsNotEmpty, i18n, supportPageRoute } =
    props;

  const [logoutModalOpen, setLogoutModalState] = useState(false);
  const [languageSubMenuOpen, setLanguageSubMenuOpen] = useState(false);
  const brandingLayout = useSelector(getBrandingLayout);

  const isLanguageSwitcherVisible = localizationService.isEuLanguageSwitcherVisible();
  const languageOptions = localizationService.getEuLanguageSwitcherOptions();
  const currentLocale = localizationService.getLocale();

  const onLogoutClick = (): void => {
    setLogoutModalState(true);
  };

  const onLanguageSelect = (opt: { i18nLanguage: string; locale: string }) => {
    const availableLocales = localizationService.getAvailableLocales();
    if (!availableLocales.includes(opt.locale)) return;
    localizationService.setEuLanguageOverride(opt.locale, true);
    setLanguageSubMenuOpen(false);
  };

  const label = isUserNameIsNotEmpty ? prepareName(`${firstName} ${lastName}`) : i18n.welcomeLabel;
  const { isLogoutDisabled } = brandingLayout?.account || {};

  return (
    <div
      role="presentation"
      className={className}
      onClick={(e: MouseEvent) => e.nativeEvent.stopImmediatePropagation()}
      data-test-id="expanded-view"
    >
      <OptionsWrapper>
        <MainMenuColumn>
          <FirstAccountOptionsItem data-test-id="full-name-block">
            <ProfileIcon
              firstLetter={firstLetter}
              lastLetter={lastLetter}
              isUserNameIsNotEmpty={isUserNameIsNotEmpty}
            />
            <FullName data-test-id="full-name-label" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(label) }} />
          </FirstAccountOptionsItem>
          {!!supportPageRoute && (
            <AccountOptionsItem data-test-id="support-search-block">
              <Link to={supportPageRoute}>{i18n.contactSupportLabel}</Link>
            </AccountOptionsItem>
          )}
          {isLanguageSwitcherVisible && (
            <LanguageMenuWrapper data-test-id="language-switcher-block">
              <AccountOptionsItem isLastRow={isLogoutDisabled && !languageSubMenuOpen}>
                <LanguageMenuTrigger
                  type="button"
                  aria-haspopup="true"
                  aria-expanded={languageSubMenuOpen}
                  {...(languageSubMenuOpen ? { 'aria-controls': 'account-language-submenu' } : {})}
                  onClick={() => setLanguageSubMenuOpen(open => !open)}
                >
                  <span>{i18n.languageLabel}</span>
                  <ChevronRightIcon aria-hidden="true">
                    <i className={`fas fa-chevron-${languageSubMenuOpen ? 'up' : 'down'}`} />
                  </ChevronRightIcon>
                </LanguageMenuTrigger>
              </AccountOptionsItem>
              {languageSubMenuOpen && (
                <LanguageSubMenu id="account-language-submenu" data-test-id="language-sub-menu" role="menu">
                  {languageOptions.map((opt, index) => (
                    <LanguageOption
                      key={opt.locale}
                      data-test-id={`language-option-${opt.locale}`}
                      isActive={currentLocale === opt.locale}
                      isAccordionClosingRow={isLogoutDisabled && index === languageOptions.length - 1}
                      onClick={() => onLanguageSelect(opt)}
                      role="menuitem"
                    >
                      <FlagIconWrap>
                        <EuLanguageFlag locale={opt.locale} />
                      </FlagIconWrap>
                      <span>{opt.label}</span>
                    </LanguageOption>
                  ))}
                </LanguageSubMenu>
              )}
            </LanguageMenuWrapper>
          )}
          {!isLogoutDisabled && (
            <AccountOptionsItem data-test-id="logout-block" isLastRow>
              <button data-test-id="logout-button" type="button" onClick={onLogoutClick}>
                {i18n.logOutLinkText}
              </button>
              <Modal
                isOpen={logoutModalOpen}
                modalHeader={i18n.logOutModalHeader}
                onRequestClose={({ response }): void => {
                  setLogoutModalState(false);
                  if (response) {
                    console.log('Closing Insights App');
                    localStorage.removeItem(USER_NAME_LOCAL_STORAGE_KEY);
                    clearEntryIntent();
                    googleAnalytics.events.portal.open('Logout');
                    document.location.replace('/api/v1/auth/logout');
                  }
                }}
                buttonSecondaryText={i18n.logOutModalCancelButtonText}
                buttonPrimaryText={i18n.logOutModalOkButtonText}
                buttonSecondarySettings={{
                  secondary: true
                }}
                buttonPrimarySettings={{
                  primary: true
                }}
              >
                <span>{i18n.logOutModalBody}</span>
              </Modal>
            </AccountOptionsItem>
          )}
        </MainMenuColumn>
      </OptionsWrapper>
    </div>
  );
};

export const AccountOptions = styled(AccountOptionsComponent)`
  /* IE 11 hack start */
  display: flex;
  flex-direction: column;
  /* IE 11 hack end */

  position: absolute;
  z-index: 101;
  right: 24px;
  margin-top: 10px;
  background-color: ${props => getColor(props, 'white')};
  width: 193px;
  @media ${deviceMedia.mobile} {
    margin-right: -14px;
  }
`;

export const AccountOptionsItem = styled.div<{ isLastRow?: boolean }>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  max-height: 38px;
  padding: 11px 10px 10px;
  border: 1px solid ${props => getColor(props, 'mediumGrey')};
  border-bottom: none;

  ${({ isLastRow }) =>
    isLastRow &&
    css`
      border-bottom: 1px solid ${props => getColor(props, 'mediumGrey')};
    `}

  & > * {
    ${textRegular};
  }

  & button,
  a {
    ${resetButtonStyles};
    ${bold};
    text-decoration: none;
    color: ${props => getColor(props, 'color1')};
    &:disabled {
      cursor: not-allowed;
      color: ${props => getColor(props, 'grey')};
    }
  }
`;

const FirstAccountOptionsItem = styled(AccountOptionsItem)`
  padding: 10px;
  max-height: 60px;
`;

const OptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const MainMenuColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const LanguageMenuWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const LanguageMenuTrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  cursor: pointer;
  ${resetButtonStyles};
  ${bold};
  color: ${props => getColor(props, 'color1')};
  ${textRegular};
`;

const ChevronRightIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  color: ${props => getColor(props, 'darkGrey')};
  flex-shrink: 0;
  svg {
    display: block;
  }
`;

const LanguageSubMenu = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: ${props => getColor(props, 'white')};
`;

const LanguageOption = styled.button.attrs({ type: 'button' })<{ isActive?: boolean; isAccordionClosingRow?: boolean }>`
  ${resetButtonStyles};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 10px;
  max-height: 38px;
  padding: 11px 10px 10px;
  ${textRegular};
  ${bold};
  color: ${props => getColor(props, 'color1')};
  cursor: pointer;
  text-align: left;
  width: 100%;
  border: 1px solid ${props => getColor(props, 'mediumGrey')};
  border-bottom: none;
  ${({ isAccordionClosingRow }) =>
    isAccordionClosingRow &&
    css`
      border-bottom: 1px solid ${props => getColor(props, 'mediumGrey')};
    `}
  &:hover {
    background-color: ${props => getColor(props, 'lightGrey')};
  }
`;

const FlagIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;
  img {
    display: block;
    flex-shrink: 0;
    object-fit: cover;
  }
`;
